/**
 * prices.ts — the post-eligibility price quote call (authed).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: prices (and POM medication names) are a POST-ELIGIBILITY surface — the
 * quote is only fetched once a patient is eligible, on the plan-selection step.
 * The gateway path doubles the segment (`/v1/prices/prices/quote`) — confirmed
 * the route requires a bearer token on stage. Prices are integer PENCE; a 0
 * means "not set yet" and renders as the launch placeholder (see lib/format).
 */
import { get, post, request } from './client';
import type {
  FormularyResponse,
  Medication,
  MedicationUpdate,
  PlanOption,
  PlanOptionsResponse,
  PlanPrice,
  PlanPriceUpsert,
  PriceChangeAuditEntry,
} from './types';

/**
 * GET /v1/prices/prices/quote?clinicSlug&productKey&months → a plan option with
 * per-term monthly prices. `months` seeds the recommended term.
 */
export function getQuote(params: {
  clinicSlug: string;
  productKey: string;
  months: number;
}): Promise<PlanOption> {
  const query = new URLSearchParams({
    clinicSlug: params.clinicSlug,
    productKey: params.productKey,
    months: String(params.months),
  });
  return get(`/v1/prices/prices/quote?${query.toString()}`, true);
}

/* ── Admin plan management (ADMIN only) ─────────────────────────────────────
 *
 * The gateway forwards /v1/prices/<rest> → prices-service /v1/<rest>, so the
 * medication registry, plan-price matrix and margin model live at the single
 * `prices` segment. Money is integer PENCE end-to-end. The D6 pack-maths gate
 * rejects a stranded-pack config with a 400 whose `message` reaches the form via
 * `parseError` (ApiError.detail).
 */

/** GET /v1/prices/medications → the medication registry. */
export function listMedications(): Promise<Medication[]> {
  return get('/v1/prices/medications', true);
}

/** POST /v1/prices/medications → create a medication (D6-gated over priced terms). */
export function createMedication(body: Medication): Promise<Medication> {
  return post('/v1/prices/medications', body, { auth: true, parseError: true });
}

/** PATCH /v1/prices/medications/:productKey → edit a medication (re-runs D6). */
export function updateMedication(productKey: string, body: MedicationUpdate): Promise<Medication> {
  return request(`/v1/prices/medications/${encodeURIComponent(productKey)}`, {
    method: 'PATCH',
    body,
    auth: true,
    parseError: true,
  });
}

/** GET /v1/prices/plan-prices[?clinicSlug] → the plan-price matrix. */
export function listPlanPrices(clinicSlug?: string): Promise<PlanPrice[]> {
  const q = clinicSlug ? `?clinicSlug=${encodeURIComponent(clinicSlug)}` : '';
  return get(`/v1/prices/plan-prices${q}`, true);
}

/**
 * POST /v1/prices/plan-prices → upsert one clinic × medication × term price
 * (D6-gated). Governance (v0.8.3): the body now carries a REQUIRED price-change
 * `reason` and an OPTIONAL `note`, recorded on the append-only price-change
 * audit. The admin drawer only calls this for a term whose price actually moved.
 */
export function upsertPlanPrice(body: PlanPriceUpsert): Promise<PlanPrice> {
  return post('/v1/prices/plan-prices', body, { auth: true, parseError: true });
}

/**
 * GET /v1/prices/plan-prices/audit?clinicSlug&productKey → the price-change
 * history for one clinic × medication, newest-first (ADMIN). Same doubled
 * `/v1/prices/...` gateway prefix as the other plan-price client fns. Read-only:
 * powers the admin drawer's "Recent price changes" panel.
 */
export function listPlanPriceAudit(
  clinicSlug?: string,
  productKey?: string,
): Promise<PriceChangeAuditEntry[]> {
  const query = new URLSearchParams();
  if (clinicSlug) query.set('clinicSlug', clinicSlug);
  if (productKey) query.set('productKey', productKey);
  const qs = query.toString();
  return get(`/v1/prices/plan-prices/audit${qs ? `?${qs}` : ''}`, true);
}

/* NOTE: the `pricing-settings` (margin-model) client fns were removed with the
 * admin margin-model UI (deleted by CTO direction, 2026-07-16). The prices
 * service still exposes the endpoints, but no portal screen calls them — so the
 * unused client wrappers are gone rather than left to rot. Re-add them here if a
 * margin-model surface ever returns. */

/* ── Plan options (post-eligibility, authed) ─────────────────────────────────
 *
 * C2.1: the plan selector shows EVERY configured medication for the clinic,
 * not one hardcoded quote. Same doubled gateway segment as the quote route.
 * Also feeds the clinician prescription builder's medication select (which
 * renders only productKey/medicationName/form from it — never a price).
 */

/** GET /v1/prices/prices/options?clinicSlug= → all plan options for a clinic. */
export function getPlanOptions(
  clinicSlug: string,
  opts?: { checkoutToken?: string; assessmentId?: string },
): Promise<PlanOptionsResponse> {
  if (opts?.checkoutToken && opts.assessmentId) {
    const query = new URLSearchParams({
      clinicSlug,
      assessmentId: opts.assessmentId,
    });
    return request(`/v1/prices/checkout/guest/options?${query.toString()}`, {
      method: 'GET',
      headers: {
        'x-checkout-token': opts.checkoutToken,
        'x-assessment-id': opts.assessmentId,
      },
    });
  }
  return get(`/v1/prices/prices/options?clinicSlug=${encodeURIComponent(clinicSlug)}`, true);
}

/* ── The clinician FORMULARY read (2026-09-12) ───────────────────────────────
 *
 * `GET /v1/prices/formulary[?productKey=]` → `{ source, medications }`.
 *
 * WHY IT IS A SEPARATE CALL FROM `getPlanOptions` AND MUST STAY ONE. Plan
 * options are the PATIENT's surface: they carry prices, and their gate admits a
 * post-eligibility patient. The formulary is the list of licensed STRENGTHS a
 * prescriber picks from, and prices-service gates it on the staff role instead —
 * a patient is refused outright rather than conditionally admitted. Hanging the
 * strengths off the options payload would put a list of every licensed strength
 * of four prescription-only medicines in front of every patient who passed the
 * BMI gate, and would put strength-shaped values on the same payload as the
 * prices — which is precisely the adjacency that let a PRICING ANCHOR be read as
 * a clinical strength and printed on prescriptions.
 *
 * NO PRICE AND NO DEFAULT travel on the response. If either ever appears, the
 * fix is in prices-service (its DTO is `.strict()`), not a filter here.
 *
 * THIS READ MUST FAIL LOUDLY. `PrescribeForm` blocks submission when it fails
 * rather than falling back to free text: a free-text strength box is the defect
 * this whole lane exists to remove, and an outage is not a reason to reinstate
 * it for an afternoon.
 */

/** GET /v1/prices/formulary[?productKey=] → the licensed variants (staff only). */
export function getFormulary(productKey?: string): Promise<FormularyResponse> {
  const q = productKey ? `?productKey=${encodeURIComponent(productKey)}` : '';
  return get(`/v1/prices/formulary${q}`, true);
}
